import React, { useEffect, useRef } from 'react';
import './ConfirmDialog.css';

/**
 * Accessible confirmation dialog to replace window.confirm()
 * Features:
 * - Keyboard navigation (Escape to cancel, Enter to confirm)
 * - Focus trap
 * - ARIA labels
 * - Customizable appearance
 * - Token-only colours (polish 4): ConfirmDialog.css; a danger confirm opens on Cancel
 */

const VARIANTS = ['default', 'danger', 'warning'];

const ConfirmDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default', // 'default', 'danger', 'warning'
  icon = null,
  loading = false
}) => {
  const dialogRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);

  // Focus management. A destructive (danger) confirm opens on Cancel, the safe answer, so an
  // Enter or Space on arrival cannot delete (customers polish 2, Polish 4 (b)).
  useEffect(() => {
    if (!isOpen) return;
    const first = variant === 'danger' ? cancelButtonRef.current : confirmButtonRef.current;
    if (first) first.focus();
  }, [isOpen, variant]);

  // Keyboard handling
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Tab') {
        // Trap focus within dialog
        const focusableElements = dialogRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements?.length) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Polish 4: every colour is a token, in ConfirmDialog.css; a variant is a class.
  const tone = VARIANTS.includes(variant) ? variant : 'default';

  const defaultIcons = {
    default: '❓',
    danger: '⚠️',
    warning: '⚡'
  };

  const displayIcon = icon || defaultIcons[tone];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      className="confirm-dialog-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div ref={dialogRef} className="confirm-dialog">
        {/* Icon */}
        <div className={`confirm-dialog-icon confirm-dialog-icon--${tone}`} aria-hidden="true">
          {displayIcon}
        </div>

        {/* Title */}
        <h2 id="dialog-title" className="confirm-dialog-title">
          {title}
        </h2>

        {/* Message */}
        <p id="dialog-description" className="confirm-dialog-message">
          {message}
        </p>

        {/* Actions */}
        <div className="confirm-dialog-actions" role="group" aria-label="Dialog actions">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            disabled={loading}
            aria-label={`${cancelText} and close dialog`}
            className="confirm-dialog-btn confirm-dialog-cancel"
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            disabled={loading}
            aria-label={loading ? 'Processing...' : confirmText}
            aria-busy={loading}
            className={`confirm-dialog-btn confirm-dialog-confirm confirm-dialog-confirm--${tone}`}
          >
            {loading && <span className="confirm-dialog-spinner" aria-hidden="true" />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook for easier usage
export const useConfirmDialog = () => {
  const [dialogState, setDialogState] = React.useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'default',
    onConfirm: () => {},
    loading: false
  });

  const confirm = ({
    title = 'Confirm Action',
    message = 'Are you sure?',
    variant = 'default',
    confirmText = 'Confirm',
    cancelText = 'Cancel'
  }) => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        title,
        message,
        variant,
        confirmText,
        cancelText,
        loading: false,
        onConfirm: () => {
          setDialogState(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setDialogState(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        }
      });
    });
  };

  const DialogComponent = () => (
    <ConfirmDialog
      isOpen={dialogState.isOpen}
      title={dialogState.title}
      message={dialogState.message}
      variant={dialogState.variant}
      confirmText={dialogState.confirmText}
      cancelText={dialogState.cancelText}
      loading={dialogState.loading}
      onConfirm={dialogState.onConfirm}
      onCancel={dialogState.onCancel}
    />
  );

  return { confirm, DialogComponent };
};

export default ConfirmDialog;
